import React from 'react';
import { Plus, Trash2, Upload, Users, Link } from 'lucide-react';
import { EditableText } from '../components/EditableText';
import { InfoNotice } from '../../components/InfoNotice';
import { ImageDropZone } from '../components/ImageDropZone';

export interface TeamMember {
  headshot: string;
  name: string;
  position: string;
  phone: string;
  email: string;
  license: string;
  bio: string;
}

export interface MeetTheTeamContent {
  headerTagline: string;
  sectionTitle: string;
  teamMembers: TeamMember[];
  teamRosterLink?: string; // Google Drive or other URL for team roster
  useTeamRosterLink?: boolean; // If true, use the link instead of manually entering team members
}

interface MeetTheTeamSectionProps {
  content: MeetTheTeamContent;
  onUpdate: (updates: Partial<MeetTheTeamContent>) => void;
  organizationId: string;
  isPreview: boolean;
  onHeadshotUpload?: (index: number, file: File) => void;
  accentColor?: string;
}

export const MeetTheTeamSection: React.FC<MeetTheTeamSectionProps> = ({
  content,
  onUpdate,
  organizationId,
  isPreview,
  onHeadshotUpload,
  accentColor,
}) => {
  const addTeamMember = () => {
    const newMember: TeamMember = {
      headshot: '',
      name: '[Team Member Name]',
      position: '[Position/Title]',
      phone: '[Phone Number]',
      email: '[Email]',
      license: '[License Number]',
      bio: '[Team member bio - This will be used on the About Us page]',
    };
    onUpdate({ teamMembers: [...content.teamMembers, newMember] });
  };

  const removeTeamMember = (index: number) => {
    const updatedMembers = content.teamMembers.filter((_, i) => i !== index);
    onUpdate({ teamMembers: updatedMembers });
  };

  const updateTeamMember = (index: number, field: keyof TeamMember, value: string) => {
    const updatedMembers = [...content.teamMembers];
    updatedMembers[index] = { ...updatedMembers[index], [field]: value };
    onUpdate({ teamMembers: updatedMembers });
  };

  const handleHeadshotClick = (index: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e: any) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file.type.startsWith('image/')) {
          if (onHeadshotUpload) {
            onHeadshotUpload(index, file);
          }
        } else {
          alert('Please select an image file');
        }
      }
    };
    input.click();
  };

  return (
    <div className="w-full bg-gray-100 py-16">
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div className="text-center mb-12">
          {/* Header Tagline - Not Editable */}
          <div className="mb-4">
            <div
              className="font-bold text-sm uppercase tracking-wider"
              style={{ color: accentColor || '#FACC15' }}
            >
              {content.headerTagline}
            </div>
          </div>
          <div className="text-gray-900 text-5xl font-bold">{content.sectionTitle}</div>
        </div>

        {/* Team Roster Link Option */}
        {!isPreview && (
          <div className="mb-8">
            <div className="flex items-start gap-3 mb-4">
              <input
                type="checkbox"
                id="useTeamRosterLink"
                checked={content.useTeamRosterLink || false}
                onChange={(e) => onUpdate({ useTeamRosterLink: e.target.checked })}
                className="mt-1 w-5 h-5 text-blue-600 cursor-pointer"
              />
              <label
                htmlFor="useTeamRosterLink"
                className="text-gray-900 font-semibold text-lg cursor-pointer"
              >
                I have a team roster document (Google Drive, Dropbox, etc.)
              </label>
            </div>

            <InfoNotice>
              {content.useTeamRosterLink
                ? 'Provide a link to your team roster document below. Our design team will use this to populate the team section on your website.'
                : "Check the box above if you have a team roster document (with photos, bios, etc.) you'd like to share instead of entering each team member manually."}
            </InfoNotice>

            {content.useTeamRosterLink && (
              <div className="mt-4">
                <label className="block text-gray-700 font-medium mb-2">
                  Team Roster Link (Google Drive, Dropbox, etc.):
                </label>
                <input
                  type="text"
                  value={content.teamRosterLink || ''}
                  onChange={(e) => onUpdate({ teamRosterLink: e.target.value })}
                  placeholder="https://drive.google.com/... or any shareable link"
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Make sure the link is publicly accessible or shared with our team. Include
                  headshots, names, titles, phone numbers, emails, licenses, and bios.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Display Team Roster Link Notice in Preview */}
        {isPreview && content.useTeamRosterLink && (
          <div className="mb-8">
            <InfoNotice>
              <div className="text-center">
                <Users className="w-8 h-8 mx-auto mb-2 text-blue-500" />
                <p className="font-semibold">Team Roster Link Provided</p>
                <p className="text-sm mt-1">
                  Your team information will be pulled from the shared document
                </p>
              </div>
            </InfoNotice>
          </div>
        )}

        {/* Info Notice about hidden fields (only show if not using roster link) */}
        {!isPreview && !content.useTeamRosterLink && (
          <div className="mb-8">
            <InfoNotice>
              Email, License, and Bio fields are collected but hidden in this preview. They will be
              used to create detailed team member profiles on your About Us page.
            </InfoNotice>
          </div>
        )}

        {/* Team Members Grid - Only show if not using roster link */}
        {!content.useTeamRosterLink && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {content.teamMembers.map((member, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-md relative">
                  {/* Delete Button */}
                  {!isPreview && content.teamMembers.length > 1 && (
                    <button
                      onClick={() => removeTeamMember(index)}
                      className="absolute top-3 right-3 p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors z-10"
                      title="Remove team member"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}

                  {/* Headshot */}
                  <div className="mb-4">
                    {member.headshot ? (
                      <div className="relative group">
                        <img
                          src={member.headshot}
                          alt={member.name}
                          className="w-full h-48 object-cover rounded-lg"
                        />
                        {!isPreview && (
                          <div className="absolute inset-0">
                            <ImageDropZone
                              onImageDrop={(file) => {
                                if (onHeadshotUpload) {
                                  onHeadshotUpload(index, file);
                                }
                              }}
                              isUploading={false}
                              label="Drop new headshot"
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-gray-200 to-gray-100 rounded-lg relative">
                        {!isPreview && (
                          <ImageDropZone
                            onImageDrop={(file) => {
                              if (onHeadshotUpload) {
                                onHeadshotUpload(index, file);
                              }
                            }}
                            isUploading={false}
                            label="Drop headshot"
                            showAlways
                          />
                        )}
                      </div>
                    )}
                  </div>

                  {/* Visible Fields */}
                  <div className="space-y-3">
                    {/* Name */}
                    <div className="text-gray-900 font-bold text-lg">
                      <EditableText
                        value={member.name}
                        onChange={(value) => updateTeamMember(index, 'name', value)}
                        isPreview={isPreview}
                      />
                    </div>

                    {/* Position */}
                    <div className="text-gray-600 text-sm">
                      <EditableText
                        value={member.position}
                        onChange={(value) => updateTeamMember(index, 'position', value)}
                        isPreview={isPreview}
                      />
                    </div>

                    {/* Phone */}
                    <div className="text-gray-700 text-sm">
                      <EditableText
                        value={member.phone}
                        onChange={(value) => updateTeamMember(index, 'phone', value)}
                        isPreview={isPreview}
                      />
                    </div>
                  </div>

                  {/* Hidden Fields - Only show in edit mode */}
                  {!isPreview && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                      <p className="text-xs text-gray-500 font-semibold uppercase mb-2">
                        For About Us Page (Hidden):
                      </p>

                      {/* Email */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Email:</label>
                        <EditableText
                          value={member.email}
                          onChange={(value) => updateTeamMember(index, 'email', value)}
                          isPreview={isPreview}
                        />
                      </div>

                      {/* License */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">License:</label>
                        <EditableText
                          value={member.license}
                          onChange={(value) => updateTeamMember(index, 'license', value)}
                          isPreview={isPreview}
                        />
                      </div>

                      {/* Bio */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Bio:</label>
                        <EditableText
                          value={member.bio}
                          onChange={(value) => updateTeamMember(index, 'bio', value)}
                          isPreview={isPreview}
                          multiline
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Add Team Member Button */}
            {!isPreview && (
              <div className="flex justify-center">
                <button
                  onClick={addTeamMember}
                  className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Add Team Member
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
